import { Button, IconButton } from "@mui/material";
import { GetDatacenter_datacenter_tla_deviceContexts_sdiInputs } from "../../views/App/__generated__/GetDatacenter";
import { Add, DeleteForeverOutlined, EditOutlined } from "@mui/icons-material";
import gql from "graphql-tag";
import { useState } from "react";
import { v4 } from "uuid";
import { useMutation } from "react-apollo";

import { toast } from "react-toastify";
import ConfirmDeletDeviceContextSdiInput from "./ConfirmDeleteDeviceContextSdiInputDialog";
import ManageDeviceContextSdiInputDialog from "./ManageDeviceContextSdiInputDialog";
import {
    UpdateDeviceContextSdiInputsMutation,
    UpdateDeviceContextSdiInputsMutationVariables,
} from "./__generated__/UpdateDeviceContextSdiInputsMutation";

interface Props {
    deviceContextId: string;
    sdiInputs: GetDatacenter_datacenter_tla_deviceContexts_sdiInputs[];
}

export const UPDATE_DEVICE_CONTEXT_SDI_INPUTS = gql`
    mutation UpdateDeviceContextSdiInputsMutation($input: UpdateDeviceContextSdiInputs!) {
        updateDeviceContextSdiInputs(input: $input) {
            id
        }
    }
`;

export interface SdiInput extends GetDatacenter_datacenter_tla_deviceContexts_sdiInputs {
    id: string;
}

export default function TLADeviceContextSdiInputs({ deviceContextId, sdiInputs }: Props) {
    const [inputs, setInputs] = useState<SdiInput[]>(sdiInputs.map((ipt) => ({ id: v4(), ...ipt })));
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const [selectedInput, setSelectedInput] = useState<SdiInput | undefined>(undefined);
    const [openManageSdiInputDialog, setOpenManageSdiInputDialog] = useState<boolean>(false);

    const [updateDeviceCtx] = useMutation<
        UpdateDeviceContextSdiInputsMutation,
        UpdateDeviceContextSdiInputsMutationVariables
    >(UPDATE_DEVICE_CONTEXT_SDI_INPUTS);

    async function onConfirmDeleteSdiInput(id: string): Promise<void> {
        try {
            const newInputs = inputs.filter((ipt) => ipt.id !== id);
            await updateDeviceCtx({
                variables: {
                    input: {
                        id: deviceContextId,
                        sdiInputs: newInputs.map(({ id, __typename, ...rest }) => rest),
                    },
                },
            });
            setInputs(newInputs);
            toast.success("SDI input removed");
        } catch {
            toast.error("Error while trying to remove SDI");
        }
        setOpenDeleteDialog(false);
    }

    async function onUpdateSdiInput(id: string, updates: Omit<SdiInput, "id">): Promise<void> {
        try {
            const newInputs = inputs.map((ipt) => {
                if (ipt.id === id) {
                    return { id, ...updates };
                } else {
                    return ipt;
                }
            });
            await updateDeviceCtx({
                variables: {
                    input: {
                        id: deviceContextId,
                        sdiInputs: newInputs.map(({ id, __typename, ...rest }) => rest),
                    },
                },
            });
            setInputs(newInputs);
            toast.success("SDI input updated");
        } catch {
            toast.error("Error while trying to update SDI");
        }
        setOpenManageSdiInputDialog(false);
    }

    async function onAddSdiInput(values: Omit<SdiInput, "id">): Promise<void> {
        try {
            const id = v4();
            const newItem: SdiInput = {
                id,
                ...values,
            };
            const newInputs = inputs.concat([newItem]);
            await updateDeviceCtx({
                variables: {
                    input: {
                        id: deviceContextId,
                        sdiInputs: newInputs.map(({ id, __typename, ...rest }) => rest),
                    },
                },
            });
            setInputs(newInputs);
            toast.success("SDI input added");
        } catch {
            toast.error("Error while trying to add SDI");
        }
        setOpenManageSdiInputDialog(false);
    }

    return (
        <div className="bg-white rounded-md px-2 py-3 flex flex-col gap-1 border-gray border-[1px]">
            <div className="w-full flex flex-row justify-between items-center mb-4">
                <strong>SDI inputs</strong>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                        setSelectedInput(undefined);
                        setOpenManageSdiInputDialog(true);
                    }}
                >
                    Add
                </Button>
            </div>
            <div className="flex flex-col gap-4 w-full">
                {inputs.map((sdiInput, i) => (
                    <div
                        key={`sdiInput-${i}`}
                        className="bg-slate-50 rounded-md border-[1px] border-slate-400 px-2 py-1 flex flex-row w-full justify-between gap-2"
                    >
                        <div>
                            <div className="font-semibold">{sdiInput.name}</div>
                            <div className="text-xs">Persistent ID: {sdiInput.persistentId}</div>
                            <div className="text-xs">Device number: {sdiInput.deviceNumber}</div>
                        </div>
                        <div>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setSelectedInput(sdiInput);
                                    setOpenManageSdiInputDialog(true);
                                }}
                            >
                                <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setSelectedInput(sdiInput);
                                    setOpenDeleteDialog(true);
                                }}
                            >
                                <DeleteForeverOutlined color="error" fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                ))}
            </div>
            {selectedInput && (
                <>
                    <ConfirmDeletDeviceContextSdiInput
                        sdiInput={selectedInput}
                        open={openDeleteDialog}
                        onClose={() => setOpenDeleteDialog(false)}
                        onConfirm={() => onConfirmDeleteSdiInput(selectedInput.id)}
                    />
                </>
            )}
            <ManageDeviceContextSdiInputDialog
                isCreate={typeof selectedInput === "undefined"}
                open={openManageSdiInputDialog}
                onClose={() => setOpenManageSdiInputDialog(false)}
                sdiInput={{
                    deviceNumber: selectedInput?.deviceNumber ?? 0,
                    name: selectedInput?.name ?? "",
                    persistentId: selectedInput?.persistentId ?? "",
                    __typename: "DeviceContextSdiInput",
                }}
                onConfirm={(values) =>
                    selectedInput ? onUpdateSdiInput(selectedInput.id, values) : onAddSdiInput(values)
                }
            />
        </div>
    );
}
